<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { onBeforeMount, ref, watch } from 'vue'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'
import dayjs from 'dayjs'

import { ResourceResponse, uuid } from '@/types/general'
import useForm from '@/hooks/use-form'
import { useAuthStore } from '@/stores/auth-store'
import { DefaultRemark, DefaultRemarksType } from '@/types/cargo-loading'
import { DropdownStringOption } from '@/types/inputs'
import { emitter } from '@/utils/mitt'
import { useLazyLoadedList } from '@/hooks/use-lazy-loaded-list'
import { MinimalCompany } from '@/types/company'
import { nameAndShortCodeString } from '@/utils/cargo-loading'

import MyInput from '@/components/my-components/form/MyInput.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyWeekDayPicker from '@/components/my-components/form/MyWeekDayPicker.vue'

interface Props {
    modelValue: boolean
    // when true it'll create the default remark for a route. When false it'll be for a loading list
    forRoute: boolean
    id?: uuid
}

interface Form {
    id: uuid | null
    providerCompanyId: uuid | null
    providerNumber: string | null
    carrierCompanyId: uuid | null
    weekday: number
    type: DefaultRemarksType | null
    departureTime: number
    loadingTime: number
    remark: string | null
}

const emit = defineEmits<{
    (e: 'close'): void
}>()

const props = defineProps<Props>()

const { t } = useI18n()
const authStore = useAuthStore()
const { data, submit, errors, reset } = useForm<Form>({
    id: props.id ?? null,
    providerCompanyId: null,
    providerNumber: null,
    carrierCompanyId: null,
    weekday: dayjs().isoWeekday() - 1,
    type: DefaultRemarksType.Truck,
    departureTime: 0,
    loadingTime: 0,
    remark: '',
})

const defaultRemark = ref<DefaultRemark>()
const departureTime = ref('00:00')
const loadingTime = ref('00:00')
const customerCompanies = useLazyLoadedList<DropdownStringOption>(fetchCustomers)
const timeFormatRegex = /^\d{2}:\d{2}$/

function closeModal() {
    emit('close')
    reset()
}

function updateTime(type: 'loading' | 'departure', value: string | number | null) {
    if (!value || typeof value !== 'string') return

    const selectedTime = type === 'loading' ? loadingTime : departureTime
    if (!timeFormatRegex.test(value)) return
    selectedTime.value = value

    const [hours, minutes] = value.split(':').map(Number)
    if (isNaN(hours) || isNaN(minutes)) return

    const time = dayjs()
        .startOf('day')
        .set('hour', hours)
        .set('minute', minutes)
        .diff(dayjs().startOf('day'), 'minutes')

    if (type === 'loading') data.loadingTime = time
    else data.departureTime = time
}

async function onSubmit() {
    let route
    if (props.id) {
        route = window.route('cl.company.default-remarks.update', {
            company: authStore.companyId,
            default_remark: props.id,
        })
    } else {
        route = window.route('cl.company.default-remarks.store', {
            company: authStore.companyId,
        })
    }

    try {
        const response = await submit<ResourceResponse<DefaultRemark>>(
            props.id ? 'PUT' : 'POST',
            route,
        )
        if (response) {
            closeModal()
            emitter.emit('fetchDefaultRemarks')
        }
    } catch (e) {
        notify({
            type: 'error',
            text: t('formError'),
        })
        throw e
    }
}

async function fetchRemark() {
    const response = await axios.get<ResourceResponse<DefaultRemark>>(
        window.route('cl.company.default-remarks.show', {
            company: authStore.companyId,
            default_remark: props.id ?? '',
        }),
    )
    defaultRemark.value = response.data.data

    const loadingTimeFormatted = dayjs()
        .startOf('day')
        .add(defaultRemark.value!.loadingTime, 'minutes')
        .format('HH:mm')
    const departureTimeFormatted = dayjs()
        .startOf('day')
        .add(defaultRemark.value!.departureTime, 'minutes')
        .format('HH:mm')

    reset({
        providerCompanyId: defaultRemark.value?.provider.id,
        providerNumber: defaultRemark.value?.providerNumber,
        carrierCompanyId: defaultRemark.value?.carrier?.id,
        weekday: defaultRemark.value?.weekday,
        type: defaultRemark.value?.type,
        remark: defaultRemark.value?.remark,
        loadingTime: defaultRemark.value!.loadingTime,
        departureTime: defaultRemark.value!.departureTime,
    })

    loadingTime.value = loadingTimeFormatted
    departureTime.value = departureTimeFormatted
}

async function fetchCustomers(): Promise<DropdownStringOption[]> {
    const response = await axios.get<MinimalCompany[]>(
        window.route('minimal.companies.customers', {
            company: authStore.companyId,
            'without-new': true,
        }),
    )
    return response.data.map((company) => ({
        label: nameAndShortCodeString(company.name, company.accountNumber ?? null),
        value: company.id,
    }))
}

function onOpen() {
    if (!props.modelValue) return

    customerCompanies.fetch()

    reset()

    if (props.id) {
        fetchRemark()
    } else {
        loadingTime.value = '00:00'
        departureTime.value = '00:00'
    }
}

onBeforeMount(onOpen)

watch(() => props.modelValue, onOpen)
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="650" @close="closeModal()">
        <MyForm class="flex flex-col space-y-4 mt-2" :errors="errors" @submit.prevent="onSubmit">
            <MyWeekDayPicker v-if="!props.forRoute" v-model="data.weekday" small name="weekday" />

            <div class="flex space-x-4">
                <MySelect
                    v-model="data.providerCompanyId"
                    name="providerCompanyId"
                    group-class="w-full"
                    :options="customerCompanies.items.value"
                    :loading="customerCompanies.fetching.value"
                    :label="t('provider')"
                    searchable
                />
                <MyInput
                    v-model="data.providerNumber"
                    name="providerNumber"
                    :label="t('providerNumber')"
                    :placeholder="t('providerNumber')"
                />
            </div>

            <MySelect
                v-if="props.forRoute"
                v-model="data.carrierCompanyId"
                :options="customerCompanies.items.value"
                :loading="customerCompanies.fetching.value"
                :label="t('carrier')"
                :placeholder="t('carrier')"
                searchable
            />

            <div v-if="!props.forRoute" class="flex w-full space-x-4">
                <MyInput
                    :model-value="loadingTime"
                    type="time"
                    name="loadingTime"
                    :label="t('loadingTime')"
                    :placeholder="t('loadingTime')"
                    class="[&::-webkit-calendar-picker-indicator]:hidden"
                    @update:model-value="(value) => updateTime('loading', value)"
                />

                <MyInput
                    :model-value="departureTime"
                    type="time"
                    name="departureTime"
                    :label="t('departureTime')"
                    :placeholder="t('departureTime')"
                    class="[&::-webkit-calendar-picker-indicator]:hidden"
                    @update:model-value="(value) => updateTime('departure', value)"
                />
            </div>

            <MyInput
                v-model="data.remark"
                name="remark"
                :label="t('remark')"
                :placeholder="t('remark')"
            />

            <MyButton
                scheme="primary"
                class="ml-auto"
                v-text="!props.id ? t('create') : t('update')"
            />
        </MyForm>
    </MyModal>
</template>
